/**
 * i18n nhẹ cho Admin Console (W11 — NFR xuyên suốt): ngôn ngữ giao diện `vi` (mặc định) | `en`, lưu `localStorage['kovix.locale']`,
 * gửi `Accept-Language` cho API (ProblemDetail/nhãn máy chủ theo ngôn ngữ ở đợt sau). Định dạng số/ngày (`format.ts`) đọc locale từ đây.
 * Chiến lược: chuỗi màn hình nghiệp vụ vẫn là tiếng Việt trong mã (BRD tiếng Việt); từ điển ở đây phủ shell (menu, topbar, hành động chung,
 * trạng thái chung) — mở rộng theo từng màn khi cần bằng `t('key')`. Không dùng thư viện ngoài.
 */
import { create } from 'zustand';

export type Locale = 'vi' | 'en';
export const LOCALES: { code: Locale; label: string; intl: string }[] = [
  { code: 'vi', label: 'Tiếng Việt', intl: 'vi-VN' },
  { code: 'en', label: 'English', intl: 'en-US' },
];

const STORAGE_KEY = 'kovix.locale';
function initialLocale(): Locale {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    return v === 'en' ? 'en' : 'vi';
  } catch {
    return 'vi';
  }
}

interface I18nState {
  locale: Locale;
  setLocale: (l: Locale) => void;
}
export const useI18n = create<I18nState>((set) => ({
  locale: initialLocale(),
  setLocale: (locale) => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* private mode */
    }
    set({ locale });
  },
}));

/** Đọc ngoài React (format, http). */
export const currentLocale = (): Locale => useI18n.getState().locale;
export const intlTag = (l: Locale = currentLocale()): string => LOCALES.find((x) => x.code === l)?.intl ?? 'vi-VN';

/** Từ điển shell — khóa ổn định, giá trị theo ngôn ngữ. Thiếu khóa → trả về tiếng Việt, thiếu nữa → chính khóa. */
const DICT: Record<string, { vi: string; en: string }> = {
  'shell.overview': { vi: 'Tổng quan', en: 'Overview' },
  'shell.workInbox': { vi: 'Công việc của tôi', en: 'My work' },
  'shell.search': { vi: 'Tìm kiếm', en: 'Search' },
  'shell.searchPlaceholder': {
    vi: 'Tìm sản phẩm, khách hàng, đơn hàng… (Ctrl+K)',
    en: 'Search products, customers, orders… (Ctrl+K)',
  },
  'shell.searchEmpty': { vi: 'Không có kết quả', en: 'No results' },
  'shell.searchHint': { vi: 'Gõ ít nhất 2 ký tự', en: 'Type at least 2 characters' },
  'shell.notifications': { vi: 'Thông báo', en: 'Notifications' },
  'shell.account': { vi: 'Tài khoản', en: 'Account' },
  'shell.signOut': { vi: 'Đăng xuất', en: 'Sign out' },
  'shell.language': { vi: 'Ngôn ngữ', en: 'Language' },
  // v151 — Admin nội bộ và Customer/CTV Portal là hai frontend độc lập, chung backend
  'shell.portalLaunch': { vi: 'Mở Portal', en: 'Open Portal' },
  'shell.portalLaunchHint': {
    vi: 'Mở Customer & CTV Portal ở tab mới — trải nghiệm khách hàng không nằm trong Admin',
    en: 'Open the Customer & CTV Portal in a new tab — the customer experience does not live inside Admin',
  },
  'shell.platformMode': { vi: 'Chế độ nền tảng', en: 'Platform mode' },
  'shell.tenantMode': { vi: 'Chế độ doanh nghiệp', en: 'Tenant mode' },
  'shell.supportReadOnly': {
    vi: 'Phiên hỗ trợ chỉ đọc — mọi thao tác ghi bị chặn',
    en: 'Read-only support session — all writes are blocked',
  },
  'shell.supportWrite': {
    vi: 'Phiên hỗ trợ có quyền ghi — mọi hành động được ghi audit',
    en: 'Support session with write access — every action is audited',
  },
  'shell.realtimeOffline': {
    vi: 'Mất kết nối thời gian thực — đang thử lại',
    en: 'Realtime connection lost — reconnecting',
  },
  // X0‑e (18/09/2026) — shell theo mockup v150: command palette Ctrl K, thu gọn menu, trợ giúp
  'shell.commandPalette': { vi: 'Tìm nhanh chức năng', en: 'Quick command' },
  'shell.commandPlaceholder': { vi: 'Tìm chức năng, module…', en: 'Find a screen or module…' },
  'shell.commandEmpty': { vi: 'Không tìm thấy chức năng phù hợp.', en: 'No matching screen.' },
  'shell.commandHint': { vi: '↑↓ chọn · ↵ mở · Esc đóng', en: '↑↓ select · ↵ open · Esc close' },
  'shell.toggleSidebar': { vi: 'Thu gọn / mở menu', en: 'Collapse / expand menu' },
  'shell.help': { vi: 'Trợ giúp', en: 'Help' },
  'shell.helpGuide': { vi: 'Hướng dẫn màn này', en: 'Guide for this screen' },
  'shell.helpGuideMissing': {
    vi: 'Chưa cấu hình địa chỉ tài liệu (VITE_KOVIX_DOCS_URL)',
    en: 'Docs URL not configured (VITE_KOVIX_DOCS_URL)',
  },
  'shell.helpShortcuts': { vi: 'Phím tắt', en: 'Keyboard shortcuts' },
  'shell.helpRoleHub': { vi: 'Bản đồ nghiệp vụ & vai trò', en: 'Business map & roles' },
  'shell.helpInbox': { vi: 'Trung tâm thông báo', en: 'Notification center' },
  'shell.shortcutPalette': { vi: 'Mở tìm nhanh chức năng', en: 'Open quick command' },
  'shell.shortcutSearch': { vi: 'Nhảy tới ô tìm dữ liệu', en: 'Jump to data search' },
  'shell.shortcutEscape': { vi: 'Đóng hộp thoại / menu', en: 'Close dialog / menu' },
  'common.save': { vi: 'Lưu', en: 'Save' },
  'common.cancel': { vi: 'Hủy', en: 'Cancel' },
  'common.close': { vi: 'Đóng', en: 'Close' },
  'common.back': { vi: 'Quay lại', en: 'Back' },
  'common.create': { vi: 'Tạo mới', en: 'Create' },
  'common.edit': { vi: 'Sửa', en: 'Edit' },
  'common.delete': { vi: 'Xóa', en: 'Delete' },
  'common.confirm': { vi: 'Xác nhận', en: 'Confirm' },
  'common.approve': { vi: 'Duyệt', en: 'Approve' },
  'common.reject': { vi: 'Từ chối', en: 'Reject' },
  'common.export': { vi: 'Xuất', en: 'Export' },
  'common.filter': { vi: 'Bộ lọc', en: 'Filters' },
  'common.all': { vi: 'Tất cả', en: 'All' },
  'common.loading': { vi: 'Đang tải…', en: 'Loading…' },
  'common.empty': { vi: 'Chưa có dữ liệu', en: 'No data yet' },
  'common.error': { vi: 'Có lỗi xảy ra', en: 'Something went wrong' },
  'common.retry': { vi: 'Thử lại', en: 'Retry' },
  'common.reload': { vi: 'Tải lại', en: 'Reload' },
  'common.staleReload': {
    vi: 'Dữ liệu đã thay đổi — tải lại rồi thao tác lại',
    en: 'Data changed — reload and try again',
  },
  'common.denied': { vi: 'Bạn không có quyền thực hiện thao tác này', en: 'You are not allowed to do this' },
  'common.yes': { vi: 'Có', en: 'Yes' },
  'common.no': { vi: 'Không', en: 'No' },
  'search.kind.PRODUCT': { vi: 'Sản phẩm', en: 'Products' },
  'search.kind.CUSTOMER': { vi: 'Khách hàng', en: 'Customers' },
  'search.kind.ORDER': { vi: 'Đơn hàng', en: 'Orders' },
  'search.kind.SUPPLIER': { vi: 'Nhà cung cấp', en: 'Suppliers' },
  'search.kind.LEAD': { vi: 'Lead', en: 'Leads' },
  'search.kind.TICKET': { vi: 'Ticket', en: 'Tickets' },
  'search.kind.WORK_ORDER': { vi: 'Lệnh sản xuất', en: 'Work orders' },
  'search.kind.PURCHASE_ORDER': { vi: 'Đơn mua', en: 'Purchase orders' },
  'search.kind.LOT': { vi: 'Lô hàng', en: 'Lots' },
  'search.kind.REPORT': { vi: 'Báo cáo', en: 'Reports' },
};

export type I18nKey = keyof typeof DICT;

function interpolate(s: string, params?: Record<string, string | number>): string {
  return params ? s.replace(/\{(\w+)\}/g, (_, k: string) => String(params[k] ?? `{${k}}`)) : s;
}

/** Dịch ngoài React (menu sinh từ dữ liệu, toast). */
export function t(
  key: I18nKey | string,
  params?: Record<string, string | number>,
  locale: Locale = currentLocale(),
): string {
  const e = DICT[key];
  return interpolate(e ? (e[locale] ?? e.vi) : key, params);
}

/** Hook — re-render khi đổi ngôn ngữ. */
export function useT() {
  const locale = useI18n((s) => s.locale);
  return (key: I18nKey | string, params?: Record<string, string | number>) => t(key, params, locale);
}

/** Nhãn menu tiếng Anh cho khóa routes.json (nguồn tiếng Việt) — chỉ phần shell; thiếu → giữ nhãn gốc. */
const NAV_EN: Record<string, string> = {
  dash: 'Overview',
  workInbox: 'My work',
  product: 'Products',
  crm: 'Customers',
  price: 'Pricing & Promotions',
  sales: 'Sales',
  social: 'Social & Leads',
  loyalty: 'Loyalty & Affiliates',
  aftersales: 'Customer care',
  dms: 'DMS',
  purchase: 'Purchasing',
  inventory: 'Inventory',
  mfg: 'Production',
  logistics: 'Logistics',
  oem: 'OEM',
  credit: 'Credit',
  payment: 'Payments',
  notify: 'Notifications',
  report: 'Reports & BI',
  files: 'Files',
  integration: 'Integrations',
  admin: 'Administration',
  platform: 'Platform',
};
export function navLabel(key: string, viLabel: string, locale: Locale = currentLocale()): string {
  return locale === 'en' ? (NAV_EN[key] ?? viLabel) : viLabel;
}
